import { Button } from "@sarva/ui";
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Brand } from "@/shared/layout/Brand";
import {
  exchangeCoreToken,
  fetchCoreToken,
  type CoreOrganisation,
  type CoreToken,
} from "../api/core";
import { DEFAULT_SIGNED_IN_ROUTE, SIGN_IN_ROUTE } from "./RequireAuth";

export const CORE_SIGN_IN_ROUTE = "/sign-in/sarva";

type Stage =
  | { kind: "checking" }
  | { kind: "choose"; token: CoreToken }
  | { kind: "entering" }
  | { kind: "done" }
  | { kind: "no-session" }
  | { kind: "no-organisation" }
  | { kind: "refused" };

/**
 * Where the Console sends someone back after signing in with Sarva (backend D315).
 *
 * One organisation: straight in. Several — an Ironwood biller works many practices' owners — a
 * picker. None: Core knows the person but they hold no PMS role anywhere, which is said plainly
 * rather than shown as a failed sign-in.
 */
export function CoreSignInPage() {
  const [stage, setStage] = useState<Stage>({ kind: "checking" });

  useEffect(() => {
    let live = true;
    void fetchCoreToken().then(async (token) => {
      if (!live) return;
      if (token === null) return setStage({ kind: "no-session" });
      if (token.organisations.length === 0) return setStage({ kind: "no-organisation" });
      const only = token.organisations.length === 1 ? token.organisations[0] : undefined;
      if (only === undefined) return setStage({ kind: "choose", token });
      setStage({ kind: "entering" });
      const ok = await exchangeCoreToken(only.id, token.accessToken);
      if (live) setStage({ kind: ok ? "done" : "refused" });
    });
    return () => {
      live = false;
    };
  }, []);

  async function enter(organisation: CoreOrganisation, token: CoreToken) {
    setStage({ kind: "entering" });
    const ok = await exchangeCoreToken(organisation.id, token.accessToken);
    setStage({ kind: ok ? "done" : "refused" });
  }

  if (stage.kind === "done") return <Navigate to={DEFAULT_SIGNED_IN_ROUTE} replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <main className="w-full max-w-sm rounded-lg border border-subtle bg-surface p-6">
        <Brand />
        <h1 className="mt-4 text-lg font-semibold">Sign in with Sarva</h1>
        {stage.kind === "checking" || stage.kind === "entering" ? (
          <p className="mt-2 text-sm text-secondary" role="status">
            Signing you in…
          </p>
        ) : null}
        {stage.kind === "choose" ? (
          <>
            <p className="mt-2 text-sm text-secondary">Choose the organisation to work in.</p>
            <ul className="mt-3 flex flex-col gap-2">
              {stage.token.organisations.map((organisation) => (
                <li key={organisation.id}>
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full justify-start"
                    onClick={() => void enter(organisation, stage.token)}
                  >
                    {organisation.name}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {stage.kind === "no-session" ? (
          <p className="mt-2 text-sm text-secondary">
            You are not signed in to Sarva in this browser.{" "}
            <Link to={SIGN_IN_ROUTE} className="underline">
              Back to sign in
            </Link>
          </p>
        ) : null}
        {stage.kind === "no-organisation" ? (
          <p className="mt-2 text-sm text-secondary">
            Your Sarva account has no access to this product yet. Ask your administrator.
          </p>
        ) : null}
        {stage.kind === "refused" ? (
          <p className="mt-2 text-sm text-secondary">
            That organisation did not accept the sign-in. Your access may not have reached it
            yet — try again in a minute, or ask your administrator.
          </p>
        ) : null}
      </main>
    </div>
  );
}
